import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import styled from 'styled-components';

import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, removeFieldError, setFieldError } from 'apps/auth/actions/formAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import ComplexPopup from 'apps/auth/components/common/Popup/ComplexPopup';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { ComplexPopupRow } from 'apps/auth/components/common/Popup/styles';
import InputPhoneWithDropdown from 'apps/auth/components/form/InputPhoneWithDropdown';
import Form from 'apps/auth/components/form-multi/Form';
import { PHONE_CALCULATOR } from 'apps/auth/components/ga/accounts/send/GaSendConstants';
import { CALCULATOR_SCREEN } from 'apps/auth/components/send/SendConstants';
import { SHOW_FORMAT_MESSAGE, SHOW_NOT_FOUND_MESSAGE } from 'apps/auth/constants/ExtraNames';
import { ACCOUNT, AMOUNT_FROM, AMOUNT_TO } from 'apps/auth/constants/fieldsName';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';

/*
 problem shift layout, temporary solution, remove after refactoring to a new phone input
 */
const InputPhoneWrapper = styled.div`
    height: 100px;
`;

const PhonePopup = (props) => {
    const submitRef = useRef(null);

    const { phone: phoneField, account: accountField } = useFields(props.formName);
    const {
        toCountry: { code: toCountryCode },
        showFormatMessage = false,
        showNotFoundMessage = false,
    } = useExtra();
    const { gotIt, continue: continueMsg, internationalMessage, recipientUserIsNotFound, popupPhoneTitle } = usePageMessages();

    const isDefaultView = !showFormatMessage && !showNotFoundMessage;
    const field = props.isLibreFlow ? accountField : phoneField;
    const { changeFormFieldValue, updateExtraByName, formName } = props;

    useEffect(() => {
        return () => {
            if (showFormatMessage || showNotFoundMessage) {
                changeFormFieldValue(formName, '', ACCOUNT)
                    .then(() => updateExtraByName(SHOW_FORMAT_MESSAGE, false))
                    .then(() => updateExtraByName(SHOW_NOT_FOUND_MESSAGE, false));
            }
        };
    }, [showFormatMessage, showNotFoundMessage, changeFormFieldValue, updateExtraByName, formName]);

    const handleServerErrors = (json) => {
        const isInputError = json?.errorText?.message;
        const isPhoneError = props.isLibreFlow ? json?.errors?.account : json?.errors?.phone;
        const isMessageError = json.extra.showFormatMessage || json.extra.showNotFoundMessage;

        return new Promise((resolve) => {
            const isError = isInputError || isPhoneError || isMessageError;
            const setError = (msg) => msg && props.setFieldError(props.formName, 'account', msg);

            if (isError) {
                if (isInputError) {
                    setError(json.errorText.message);
                } else if (isPhoneError) {
                    setError(isPhoneError?.body);
                }
            }

            resolve(isError);
        });
    };

    const clear = () => {
        return new Promise((resolve) => resolve(props.changeFormFieldValue(props.formName, '', ACCOUNT))).then(() => {
            props.updateExtraByName(SHOW_FORMAT_MESSAGE, false);
            props.updateExtraByName(SHOW_NOT_FOUND_MESSAGE, false);
        });
    };

    const onSubmitHandler = (fields, json) => {
        handleServerErrors(json).then((isError) => {
            if (!isError) {
                props.changeFormFieldValue(props.formName, null, AMOUNT_FROM);
                props.changeFormFieldValue(props.formName, null, AMOUNT_TO);
                props.removeFieldError(props.formName, AMOUNT_FROM);
                props.removeFieldError(props.formName, AMOUNT_TO);
                props.popupRef.current?.close();
                props.isLibreFlow ? props.setScreen(CALCULATOR_SCREEN) : props.setScreen(PHONE_CALCULATOR);
            }
        });
    };

    const disabled = !(field.value && field.value.length >= 6 && !field.isError);

    const closeMessage = () => clear();

    const getErrorMessage = () => {
        if (showFormatMessage) {
            return <DangerouslyHtml dangerouslyHtml={internationalMessage} />;
        }
        if (showNotFoundMessage) {
            return recipientUserIsNotFound;
        }
    };

    const renderDefaultView = () => (
        <>
            <ComplexPopupRow>{popupPhoneTitle}</ComplexPopupRow>
            <InputPhoneWrapper>
                <InputPhoneWithDropdown
                    formName={props.formName}
                    field={field}
                    defaultCountry={toCountryCode}
                    submitRef={submitRef}
                    submitByEnter
                    focus
                    iso2={toCountryCode}
                    isNonEditableCountry
                />
            </InputPhoneWrapper>
        </>
    );

    return (
        <Form
            formName={props.formName}
            action={props.action}
            onSubmitHandler={onSubmitHandler}
            validateForm={false}
            getCanonicalPath={true}
            submitByEnter={true}
        >
            {isDefaultView && (
                <ComplexPopup
                    textCenter
                    primaryButtonType="submit"
                    primaryButtonText={continueMsg}
                    primaryButtonDisabled={disabled}
                    formRef={submitRef}
                    popupRef={props.popupRef}
                >
                    {renderDefaultView()}
                </ComplexPopup>
            )}
            {!isDefaultView && (showFormatMessage || showNotFoundMessage) && (
                <SimplePopup text={getErrorMessage()} popupRef={props.popupRef}>
                    <PopupButton type="submit" iconName="check" text={gotIt} onClick={closeMessage} />
                </SimplePopup>
            )}
        </Form>
    );
};

PhonePopup.propTypes = {
    action: PropTypes.string.isRequired,
    formName: PropTypes.string.isRequired,
    setScreen: PropTypes.func.isRequired,
};

export default connect(null, { updateExtraByName, setFieldError, changeFormFieldValue, removeFieldError })(PhonePopup);
